import { StorageItemFragment } from '@sketch/gql-types'
import { useLocation } from 'react-router'
import { isEqual } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type StorageItem = Pick<StorageItemFragment, '__typename' | 'identifier'>
type StorageItemType = StorageItem['__typename']

type SelectionContextType = {
  selectedItems: StorageItem[]
  toggleItemSelection: (item: StorageItem, state?: boolean) => void
  clearSelection: () => void
  showSelectionCheckbox: boolean
  toggleShowCheckbox: () => void
  hasSelectedItems: boolean
  bulkItemSelection: (
    setGetter: (
      itemsType: Record<string, StorageItemType>,
      itemsIdSet: Set<string>
    ) => { itemsType: Record<string, StorageItemType>; itemsIdSet: Set<string> }
  ) => void
  setShowCheckbox: (state: boolean) => void
  isSelected: (item: StorageItem) => boolean
}

const SelectionContext = React.createContext<SelectionContextType>({
  clearSelection: () => {},
  selectedItems: [],
  toggleItemSelection: () => {},
  showSelectionCheckbox: false,
  toggleShowCheckbox: () => {},
  hasSelectedItems: false,
  bulkItemSelection: () => [],
  setShowCheckbox: () => {},
  isSelected: () => {
    return false
  },
})

export const SelectionContextProvider = (
  props: React.PropsWithChildren<{}>
) => {
  const { children } = props
  const [itemsType, setItemsType] = useState<Record<string, StorageItemType>>(
    {}
  )

  const location = useLocation()

  const itemsTypePerIdentifier = useRef(new Set<string>())
  const [isShowingSelectionCheckbox, setShowingSelectionCheckbox] =
    useState(false)

  const toggleItemSelection = useCallback(
    (item: StorageItem, newState?: boolean) => {
      setItemsType(itemsType => {
        const newItemsType = { ...itemsType }

        if (
          itemsTypePerIdentifier.current.has(item.identifier) &&
          newState !== true
        ) {
          itemsTypePerIdentifier.current.delete(item.identifier)
          delete newItemsType[item.identifier]
        } else if (newState !== false) {
          itemsTypePerIdentifier.current.add(item.identifier)
          newItemsType[item.identifier] = item.__typename
        }

        return newItemsType
      })
    },
    []
  )

  const clearSelection = useCallback(() => {
    setItemsType({})
    itemsTypePerIdentifier.current.clear()
  }, [])

  const toggleShowCheckbox = useCallback(() => {
    setShowingSelectionCheckbox(forceSelection => {
      const updateForceSelection = !forceSelection

      // If we are turning off the force selection, also clear the current selection
      if (!updateForceSelection) {
        clearSelection()
      }

      return updateForceSelection
    })
  }, [clearSelection])

  const isSelected = useCallback(
    (item: StorageItem) => {
      return (
        itemsTypePerIdentifier.current.has(item.identifier) &&
        itemsType[item.identifier] === item.__typename
      )
    },
    [itemsType]
  )

  useEffect(() => {
    clearSelection()
  }, [location.pathname, clearSelection])

  const contextValue: SelectionContextType = useMemo(() => {
    const selectedItems: StorageItem[] = []
    itemsTypePerIdentifier.current.forEach(identifier => {
      selectedItems.push({
        __typename: itemsType[identifier],
        identifier,
      })
    })

    const bulkItemSelection: SelectionContextType['bulkItemSelection'] =
      setGetter => {
        const newSelectedItems = setGetter(
          itemsType,
          itemsTypePerIdentifier.current
        )

        if (
          isEqual(
            newSelectedItems.itemsIdSet,
            itemsTypePerIdentifier.current
          ) &&
          isEqual(newSelectedItems.itemsType, itemsType)
        ) {
          return
        }

        itemsTypePerIdentifier.current = newSelectedItems.itemsIdSet
        setItemsType(newSelectedItems.itemsType)
      }

    const selectedItemsLength = selectedItems.length
    const hasSelectedItems = selectedItemsLength > 0

    return {
      selectedItems,
      isSelected,
      toggleItemSelection,
      clearSelection,
      toggleShowCheckbox,
      showSelectionCheckbox: isShowingSelectionCheckbox || hasSelectedItems,
      hasSelectedItems,
      setShowCheckbox: setShowingSelectionCheckbox,
      bulkItemSelection,
    }
  }, [
    isSelected,
    toggleItemSelection,
    clearSelection,
    toggleShowCheckbox,
    isShowingSelectionCheckbox,
    itemsType,
  ])

  const { hasSelectedItems } = contextValue

  /**
   * Handle Escape key to clear selection
   * when there are selected items
   */
  useEffect(() => {
    if (!hasSelectedItems) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearSelection()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [clearSelection, hasSelectedItems])

  return (
    <SelectionContext.Provider value={contextValue}>
      {children}
    </SelectionContext.Provider>
  )
}

export const useSelectionContext = () => React.useContext(SelectionContext)
